import {
  Button,
  FormControl,
  FormLabel,
  Stack,
  MenuItem as MenuItemMui,
  Select,
} from '@mui/material';
import type { Role, Viewer, Project } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { rolesComparer } from '@/configs/roles';
import { useRoles } from '@/contexts/useRoles';
import { useUser } from '@/contexts/useUser';

function getAvailableRoles(
  roleToProjectsMappings: Viewer['roleToProjectsMappings'] | undefined,
  rolesMap: Record<string, Role>
) {
  return Object.keys(roleToProjectsMappings || {})
    .reduce<Role[]>((agg, entry) => {
      const role = rolesMap[entry];
      if (role) {
        agg.push(role);
      }

      return agg;
    }, [])
    .sort(rolesComparer);
}

export const RolesSelect = () => {
  const { user, changeRole } = useUser();
  const { rolesMap } = useRoles();
  const [isChanging, setIsChanging] = useState(false);

  const availableRoles = getAvailableRoles(
    user?.roleToProjectsMappings,
    rolesMap
  );

  return (
    <Select
      sx={{
        borderRadius: '4px',
        height: '36px',
        background: 'var(--mui-palette-custom-BLACK-03)',

        '&:hover': {
          backgroundColor: `var(--mui-palette-custom-BLACK-02) !important`,
        },

        '& .MuiSelect-select:hover': {
          backgroundColor: 'transparent !important',
        },

        '& .MuiMenu-paper': {
          backgroundColor: 'var(--mui-palette-custom-BLACK-01)',
          boxShadow: `0px 10px 30px 0px var(--mui-palette-custom-BLACK-02)`,
          color: 'white',
        },

        '& .MuiMenuItem-root': {
          color: 'white',

          '&:hover': {
            backgroundColor: `var(--mui-palette-custom-BLACK-02) !important`,
          },

          '&.Mui-selected': {
            backgroundColor: 'white !important',
            color: `var(--mui-palette-custom-BLACK-01) !important`,
          },
        },
      }}
      className='text-white'
      MenuProps={{ disablePortal: true }}
      size='small'
      variant='standard'
      value={user?.selectedRole.id || ''}
      disabled={isChanging}
      onChange={(event) => {
        setIsChanging(true);
        void Promise.resolve()
          .then(async () => {
            if (event.target.value !== user?.selectedRole.id) {
              await changeRole(event.target.value);
            }
          })
          .then(
            () => window.location.reload(),
            () => window.location.reload()
          );
      }}
    >
      {availableRoles.map((role) => (
        <MenuItemMui key={role.id} value={role.id}>
          {role.name}
        </MenuItemMui>
      ))}
    </Select>
  );
};

export const ChangeScope = ({
  onCancel,
  projects,
}: {
  onCancel: () => void;
  projects: Project[];
}) => {
  const { user, currentProjectId, changeProject, changeRole } = useUser();
  const { rolesMap } = useRoles();

  const [selectedProjectId, setSelectedProjectId] = useState(currentProjectId);
  const [selectedRole, setSelectedRole] = useState(user?.selectedRole);
  const [isChanging, setIsChanging] = useState(false);

  const availableRoles = getAvailableRoles(
    user?.roleToProjectsMappings,
    rolesMap
  );

  return (
    <>
      <FormControl size='small' className='flex-1'>
        <FormLabel>Select project</FormLabel>
        <Select
          MenuProps={{ disablePortal: true }}
          size='small'
          value={selectedProjectId}
          onChange={(event) => {
            setSelectedProjectId(event.target.value);
            const availableRoles = getAvailableRoles(
              user?.roleToProjectsMappings,
              rolesMap
            );
            if (availableRoles.every((x) => x.id !== selectedRole?.id)) {
              setSelectedRole(availableRoles[0]);
            }
          }}
        >
          {projects
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((project) => (
              <MenuItemMui key={project.id} value={project.id}>
                {project.name}
              </MenuItemMui>
            ))}
        </Select>
      </FormControl>
      <FormControl size='small' className='flex-1'>
        <FormLabel>Select role</FormLabel>
        <Select
          MenuProps={{ disablePortal: true }}
          size='small'
          value={selectedRole?.id || ''}
          onChange={(event) => setSelectedRole(rolesMap[event.target.value])}
        >
          {availableRoles.map((role) => (
            <MenuItemMui key={role.id} value={role.id}>
              {role.name}
            </MenuItemMui>
          ))}
        </Select>
      </FormControl>
      <Stack direction='row' justifyContent='end'>
        <Button className='mr-[12px]' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant='outlined'
          disabled={
            isChanging ||
            (selectedRole?.id === user?.selectedRole.id &&
              selectedProjectId === currentProjectId)
          }
          onClick={() => {
            setIsChanging(true);
            void Promise.resolve()
              .then(async () => {
                if (
                  selectedProjectId &&
                  selectedProjectId !== currentProjectId
                ) {
                  await changeProject(selectedProjectId);
                }
              })
              .then(async () => {
                if (selectedRole && selectedRole.id !== user?.selectedRole.id) {
                  await changeRole(selectedRole.id);
                }
              })
              .then(
                () => window.location.reload(),
                () => window.location.reload()
              );
          }}
        >
          Change
        </Button>
      </Stack>
    </>
  );
};
